import { createSlice } from '@reduxjs/toolkit';
import { createAppThunk } from '../createAppThunk';

import type {
  AssessmentCycle,
  CreateAssessmentCycleDto,
  UpdateAssessmentCycleNoteDto,
} from '@/types/Api';
import {
  closeAssessmentCycle,
  createAssessmentCycle,
  finalizeAssessmentCycle,
  getAssessmentCycle,
  listAssessmentCycles,
  updateAssessmentCycleNote,
} from '@/lib/apiClient';
import type { NormalizedError } from '@/types/NormalizedError';

interface MhRiskAssessmentState {
  cycles: AssessmentCycle[];
  currentCycle: AssessmentCycle | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  fetchStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: NormalizedError | null;
  fetchError: NormalizedError | null;
}

const initialState: MhRiskAssessmentState = {
  cycles: [],
  currentCycle: null,
  status: 'idle',
  fetchStatus: 'idle',
  error: null,
  fetchError: null,
};

export const listCycles = createAppThunk<
  AssessmentCycle[] | undefined,
  { orgId: string; token: string }
>('mhRiskAssessment/listCycles', async ({ orgId, token }, thunkAPI) => {
  const response = await listAssessmentCycles(orgId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const getCycle = createAppThunk<
  AssessmentCycle | undefined,
  { orgId: string; cycleId: string; token: string }
>('mhRiskAssessment/getCycle', async ({ orgId, cycleId, token }, thunkAPI) => {
  const response = await getAssessmentCycle(orgId, cycleId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const createCycle = createAppThunk<
  AssessmentCycle | undefined,
  { orgId: string; data: CreateAssessmentCycleDto; token: string }
>('mhRiskAssessment/createCycle', async ({ orgId, data, token }, thunkAPI) => {
  const response = await createAssessmentCycle(orgId, data, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const updateCycleNote = createAppThunk<
  AssessmentCycle | undefined,
  { orgId: string; cycleId: string; data: UpdateAssessmentCycleNoteDto; token: string }
>('mhRiskAssessment/updateCycleNote', async ({ orgId, cycleId, data, token }, thunkAPI) => {
  const response = await updateAssessmentCycleNote(orgId, cycleId, data, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const closeCycle = createAppThunk<
  AssessmentCycle | undefined,
  { orgId: string; cycleId: string; token: string }
>('mhRiskAssessment/closeCycle', async ({ orgId, cycleId, token }, thunkAPI) => {
  const response = await closeAssessmentCycle(orgId, cycleId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

export const finalizeCycle = createAppThunk<
  AssessmentCycle | undefined,
  { orgId: string; cycleId: string; token: string }
>('mhRiskAssessment/finalizeCycle', async ({ orgId, cycleId, token }, thunkAPI) => {
  const response = await finalizeAssessmentCycle(orgId, cycleId, token);
  if (response.error) return thunkAPI.rejectWithValue(response.error);
  return response.data;
});

const mhRiskAssessmentSlice = createSlice({
  name: 'mhRiskAssessment',
  initialState,
  reducers: {
    clearCurrentCycle: (state) => {
      state.currentCycle = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // List cycles
      .addCase(listCycles.pending, (state) => {
        state.fetchStatus = 'loading';
        state.fetchError = null;
      })
      .addCase(listCycles.fulfilled, (state, action) => {
        state.fetchStatus = 'succeeded';
        state.cycles = action.payload as AssessmentCycle[];
      })
      .addCase(listCycles.rejected, (state, action) => {
        state.fetchStatus = 'failed';
        state.fetchError = action.payload ?? null;
      })
      // Get cycle
      .addCase(getCycle.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(getCycle.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentCycle = action.payload as AssessmentCycle;
      })
      .addCase(getCycle.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? null;
      })
      // Create cycle
      .addCase(createCycle.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createCycle.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentCycle = action.payload as AssessmentCycle;
        state.cycles.unshift(action.payload as AssessmentCycle);
      })
      .addCase(createCycle.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? null;
      })
      // Update cycle note
      .addCase(updateCycleNote.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateCycleNote.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentCycle = action.payload as AssessmentCycle;
        const index = state.cycles.findIndex((cycle) => cycle.id === action.payload?.id);
        if (index !== -1 && action.payload) {
          state.cycles[index] = action.payload as AssessmentCycle;
        }
      })
      .addCase(updateCycleNote.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? null;
      })
      // Close cycle
      .addCase(closeCycle.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(closeCycle.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentCycle = action.payload as AssessmentCycle;
        const index = state.cycles.findIndex((cycle) => cycle.id === action.payload?.id);
        if (index !== -1 && action.payload) {
          state.cycles[index] = action.payload as AssessmentCycle;
        }
      })
      .addCase(closeCycle.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? null;
      })
      // Finalize cycle
      .addCase(finalizeCycle.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(finalizeCycle.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentCycle = action.payload as AssessmentCycle;
        const index = state.cycles.findIndex((cycle) => cycle.id === action.payload?.id);
        if (index !== -1 && action.payload) {
          state.cycles[index] = action.payload as AssessmentCycle;
        }
      })
      .addCase(finalizeCycle.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? null;
      });
  },
});

export const { clearCurrentCycle } = mhRiskAssessmentSlice.actions;

export default mhRiskAssessmentSlice.reducer;
